/**
 * The library's icon set, and where its files live.
 *
 * `DEFAULT_ICON_LIST` names each icon by set and variant; `iconPath` turns a
 * pair into the URL under `internalData.imgDir`.
 */

import { internalData } from "../store/store.ts";
import { devlog } from "../util/utils.ts";

export interface IconListing {
  [iconName: string]: { [iconVariant: string]: string }
}

export const DEFAULT_ICON_LIST: IconListing = {
  'toggle-workpiece-view': {
    default: 'features/viz-eye-open-blk.svg',
    hidden: 'features/viz-eye-closed-blk.svg'
  },
  'user-options': {
    default: 'user.png',
  },
  'transfer-mesh': {
    default: 'features/next-b.svg'
  },
  'help': {
    default: 'features/question-2.svg'
  },
  'info': {
    default: 'features/info.svg'
  },
  'close': {
    default: 'icons/close.svg'
  },
  'settings': {
    default: 'icons/gear.svg'
  }
}

/**
 * Full path to an icon, or undefined when the set or variant is unknown.
 *
 * Resolved per call rather than built once, because imgDir is a live getter
 * that changes with useLocalAssets. Set warnMissing for a lookup that is
 * meant to succeed; the name-attribute fallbacks try speculatively and stay
 * quiet.
 */
export function iconPath(
  set: string | null | undefined,
  variant = 'default',
  warnMissing = false,
): string | undefined {
  if (!set) return undefined;
  const listing = DEFAULT_ICON_LIST[set];
  if (!listing) {
    if (warnMissing) devlog(`iconPath: icon set "${set}" not found`);
    return undefined;
  }
  const file = listing[variant];
  if (!file) {
    if (warnMissing) devlog(`iconPath: variant "${variant}" not found for "${set}"`);
    return undefined;
  }
  return `${internalData.imgDir}/${file}`;
}
